import { useCallback, useEffect, useId, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { ModerationFieldAvailability } from '../dashboardTypes';

export type ModerationReadyState = 'live' | 'snapshot' | 'partial' | 'not-ready';

const DEFAULT_HINT: Record<Exclude<ModerationReadyState, 'live'>, string> = {
  snapshot: 'This figure is a live snapshot (queue or current state), not scoped to the selected date range.',
  partial:
    'Some data for this date range is incomplete. Live figures still show; missing sources are left blank instead of inventing zeros.',
  'not-ready': 'This figure is not available yet for the selected date range.',
};

export function moderationReadyState(
  availability?: ModerationFieldAvailability,
): ModerationReadyState {
  if (availability == null || availability === 'AVAILABLE') return 'live';
  if (availability === 'SNAPSHOT') return 'snapshot';
  if (availability === 'INCOMPLETE') return 'partial';
  return 'not-ready';
}

/** True when the metric should display its numeric value (including snapshots and partials). */
export function moderationShowsValue(state: ModerationReadyState): boolean {
  return state !== 'not-ready';
}

export function NotReadyBadge({
  state = 'not-ready',
  className,
  label,
  hint,
  interactive = true,
}: {
  state?: ModerationReadyState;
  className?: string;
  label?: string;
  hint?: string;
  /** When false, render a non-focusable badge (e.g. inside a clickable row). */
  interactive?: boolean;
}) {
  if (state === 'live') return null;
  const text =
    label ??
    (state === 'snapshot' ? 'Snapshot' : state === 'partial' ? 'Partial' : 'Not ready');
  const tooltip = hint?.trim() || DEFAULT_HINT[state];

  const badge = (
    <Badge
      variant="outline"
      className={cn(
        'text-[10px] font-medium uppercase tracking-wide',
        interactive && 'cursor-help',
        state === 'snapshot' && 'border-slate-400/40 bg-slate-500/10 text-slate-300',
        state === 'partial' && 'border-amber-500/40 bg-amber-500/10 text-amber-300',
        state === 'not-ready' && 'border-sky-500/40 bg-sky-500/10 text-sky-300',
        className,
      )}
    >
      {text}
    </Badge>
  );

  if (!interactive) return badge;

  return (
    <HoverHint text={tooltip} label={`${text}. ${tooltip}`}>
      {badge}
    </HoverHint>
  );
}

export function DashboardBusy({
  label = 'Loading…',
  className,
}: {
  label?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex h-48 items-center justify-center gap-2 text-sm text-muted-foreground',
        className,
      )}
    >
      <Loader2 className="size-4 animate-spin" aria-hidden />
      {label}
    </div>
  );
}

function HoverHint({
  text,
  label,
  children,
}: {
  text: string;
  label: string;
  children: ReactNode;
}) {
  const tipId = useId();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const place = useCallback((el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    setPos({ x: rect.left + rect.width / 2, y: rect.top });
  }, []);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  return (
    <>
      <span
        className="inline-flex outline-none"
        aria-label={label}
        aria-describedby={open ? tipId : undefined}
        onMouseEnter={(event) => {
          place(event.currentTarget);
          setOpen(true);
        }}
        onMouseMove={(event) => place(event.currentTarget)}
        onMouseLeave={() => setOpen(false)}
        onFocus={(event) => {
          place(event.currentTarget);
          setOpen(true);
        }}
        onBlur={() => setOpen(false)}
        tabIndex={0}
      >
        {children}
      </span>
      {open && typeof document !== 'undefined'
        ? createPortal(
            <div
              id={tipId}
              role="tooltip"
              className="pointer-events-none fixed z-[100] max-w-[260px] -translate-x-1/2 -translate-y-[calc(100%+8px)] rounded-md px-3 py-2 text-left shadow-lg"
              style={{ left: pos.x, top: pos.y, backgroundColor: '#1e2a44' }}
            >
              <p className="text-[12px] leading-4 text-white">{text}</p>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
